import { ReactNode, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { SHOPPING_PREFERENCES_ROUTE } from '../features/shopping-preferences/shopping-preferences.constant'
import { orderSelector } from '../redux-toolkits/orders/order.reducer'
import { PLATFORM_COST_METRICS_ROUTE } from '../redux-toolkits/platform-cost-metrics/platform-cost-metrics.constant'
import { useAppSelector } from './useAppSelector'
import { usePermissions } from './usePermissions'

interface SubMenu {
  title: string
  href: string
  hidden?: boolean
}

export const useSideNav = () => {
  const { isNewOrderReceived } = useAppSelector(orderSelector)

  const { hasPermissions } = usePermissions()

  const [navItems, setNavItems] = useState<
    Array<{
      text: string
      icon: string
      href: string
      hidden?: boolean
      subMenu: SubMenu[] | []
      notificationElement?: ReactNode
    }>
  >([])
  const [subMenuOpen, setSubMenuOpen] = useState<{
    state: boolean
    index: number | null
  }>({
    state: false,
    index: null,
  })

  const { pathname } = useLocation()

  useEffect(() => {
    setNavItems([
      {
        text: 'Dashboard',
        icon: 'dashboard-icon',
        href: '/dashboard',
        hidden: !hasPermissions(['view_dashboard']),
        subMenu: [],
      },
      {
        text: 'Channel Curation',
        icon: 'house-line',
        href: '',
        hidden: !hasPermissions(['view_homepage_section']),
        subMenu: [
          {
            title: 'Homepage Curation',
            href: '/channel-curation',
            hidden: !hasPermissions(['view_homepage_section']),
          },
          {
            title: 'Browse Curation',
            href: '/channel-curation/browse',
            hidden: !hasPermissions(['view_homepage_section']),
          },
          {
            title: 'Nav Menu',
            href: '/channel-curation/nav-menu',
            hidden: !hasPermissions(['view_homepage_section']),
          },
        ],
      },
      {
        text: 'Discover',
        icon: 'house-line',
        href: '/discover',
        hidden:
          !hasPermissions(['update_system_settings']) &&
          !hasPermissions(['export_reports']),
        subMenu: [],
      },
      {
        text: 'Products',
        icon: 'product-icon',
        href: '',
        hidden:
          !hasPermissions(['view_product']) &&
          !hasPermissions(['view_list']) &&
          !hasPermissions(['view_article']),
        subMenu: [
          {
            title: 'All Products',
            href: '/products',
            hidden: !hasPermissions(['view_product']),
          },
          {
            title: 'Essentials',
            href: '/lists/curated',
            hidden: !hasPermissions(['view_list']),
          },
          {
            title: 'Good Lists',
            href: '/lists/articles',
            hidden: !hasPermissions(['view_article']),
          },
          // {
          //   title: 'Discover',
          //   href: '/discover',
          // },
        ],
      },
      {
        text: 'Events',
        icon: 'ticket',
        href: '/events',
        hidden: !hasPermissions(['add_product']),
        subMenu: [],
      },
      {
        text: 'Orders',
        icon: 'order-icon',
        href: `/orders`,
        subMenu: [],
        hidden: !hasPermissions(['view_order']),
        notificationElement: isNewOrderReceived ? (
          <div className=" w-2 h-2 bg-primary rounded-full animate-ping" />
        ) : undefined,
      },
      {
        text: 'Creators',
        icon: 'storefront',
        href: '/creators',
        hidden: !hasPermissions(['view_vendor']),
        subMenu: [],
      },
      {
        text: 'Fulfilment Center',
        icon: 'warehouse',
        href: '/fulfilment-center',
        hidden: !hasPermissions(['view_fulfillment_centres']),
        subMenu: [],
      },
      {
        text: 'POS Retail',
        icon: 'tag',
        href: '/pos',
        hidden: !hasPermissions(['view_pos']),
        subMenu: [],
      },
      {
        text: 'Loyalty Program',
        icon: 'seal-percent',
        href: '',
        hidden:
          !hasPermissions(['view_discounts']) &&
          !hasPermissions(['view_referrals']),
        subMenu: [
          {
            title: 'Discounts',
            href: `/discounts`,
            hidden: !hasPermissions(['view_discounts']),
          },
          {
            title: 'Vouchers',
            href: `/vouchers`,
            hidden: !hasPermissions(['view_voucher_metrics']),
          },
          {
            title: 'Referrals',
            href: `/referrals`,
            hidden: !hasPermissions(['view_referrals']),
          },
          {
            title: 'Earning Partners',
            href: '/earning-partners',
            hidden: !hasPermissions(['view_referrals']),
          },
        ],
      },
      {
        text: 'Finance',
        icon: 'hand-coins',
        href: '',
        hidden:
          !hasPermissions(['view_settlements']) &&
          !hasPermissions(['view_transactions']) &&
          !hasPermissions(['view_refunds']) &&
          !hasPermissions(['view_revenue_metrics']),
        subMenu: [
          {
            title: 'Settlement',
            href: '/settlement',
            hidden: !hasPermissions(['view_settlements']),
          },
          {
            title: 'Transactions',
            href: '/transactions',
            hidden: !hasPermissions(['view_transactions']),
          },
          {
            title: 'Refunds',
            href: '/refunds',
            hidden: !hasPermissions(['view_refunds']),
          },
          {
            title: 'Platform Cost Metrics',
            href: PLATFORM_COST_METRICS_ROUTE,
            hidden: !hasPermissions(['view_revenue_metrics']),
          },
        ],
      },

      {
        text: 'CRM',
        icon: 'customers-icon',
        href: '',
        hidden: !hasPermissions(['view_customers']),
        subMenu: [
          {
            title: 'Customers',
            href: '/customers',
            hidden: !hasPermissions(['view_customers']),
          },
          {
            title: 'Shopping Preferences',
            href: SHOPPING_PREFERENCES_ROUTE.BASE,
            hidden: !hasPermissions(['view_customers']),
          },
          {
            title: 'Customers Feedback',
            href: '/feedback',
            hidden: !hasPermissions(['view_customers_feedback']),
          },
          // {
          //   title: 'Waitlist',
          //   href: '/waitlist',
          // },
        ],
      },
      {
        text: 'Settings',
        icon: 'gear-icon',
        href: '/settings',
        hidden: !hasPermissions(['update_system_settings']),
        subMenu: [],
      },
      {
        text: 'Audit Logs',
        icon: 'contact-icon',
        href: '/audit-log',
        hidden: !hasPermissions(['view_audits_logs']),
        subMenu: [],
      },
    ])
  }, [isNewOrderReceived, pathname])

  return { navItems, subMenuOpen, setSubMenuOpen }
}
